import { format, parseISO } from "date-fns";

import type {
  BillingPaymentMethod,
  BillingStatus,
  BillingTier,
} from "@/schemas/billing";

import env from "./environment";
import { localeFor } from "./formatters";

/** Employer subscription billing feature flag (VITE_SUBSCRIPTION_BILLING). */
export const SUBSCRIPTION_BILLING_ENABLED =
  env.VITE_SUBSCRIPTION_BILLING === "true";

/** Path suffix of the payment-resolution route (`/app/$role/billing/resolve`). */
export const BILLING_RESOLVE_PATH_SUFFIX = "/billing/resolve";

// ---------------------------------------------------------------------------
// Gate decision
// ---------------------------------------------------------------------------

/**
 * Whether billing UI is shown to the signed-in user. The build flag only means
 * "the frontend may ask"; billing is per-account opt-in on the backend
 * (business.billing_enabled), reported as `billing/status.enabled`. Loading,
 * errors and non-employers all count as not visible.
 */
export function isBillingVisible(input: {
  flagEnabled: boolean;
  role: "worker" | "employer" | undefined;
  status: Pick<BillingStatus, "enabled"> | undefined;
}): boolean {
  return (
    input.flagEnabled &&
    input.role === "employer" &&
    input.status?.enabled === true
  );
}

export type BillingGateInput = {
  /** Feature flag. */
  flagEnabled: boolean;
  role: "worker" | "employer" | undefined;
  /** Undefined while loading / on error — never redirect without data. */
  status: Pick<BillingStatus, "enabled" | "blocked" | "actionRequired"> | undefined;
  pathname: string;
  /** "Remind me later" already used in this browser session. */
  dismissedThisSession: boolean;
};

/**
 * Whether the owner app should send the user to the billing resolution page.
 * - blocked → always (every route), the app can't be used until resolved;
 * - actionRequired → until dismissed once in this browser/login session.
 * Never redirects workers, while already on the resolution page, or during
 * setup / onboarding.
 */
export function shouldRedirectToBillingResolve(input: BillingGateInput): boolean {
  const { flagEnabled, role, status, pathname, dismissedThisSession } = input;
  if (!status || !isBillingVisible({ flagEnabled, role, status })) return false;
  if (isBillingResolvePath(pathname)) return false;
  if (isSetupPath(pathname)) return false;
  if (status.blocked) return true;
  return status.actionRequired && !dismissedThisSession;
}

export function isBillingResolvePath(pathname: string): boolean {
  return stripTrailingSlash(pathname).endsWith(BILLING_RESOLVE_PATH_SUFFIX);
}

export function isSetupPath(pathname: string): boolean {
  return /\/setup(\/|$)/.test(pathname);
}

function stripTrailingSlash(p: string): string {
  return p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p;
}

// ---------------------------------------------------------------------------
// Session "remind me later" flag
// ---------------------------------------------------------------------------

/**
 * sessionStorage key recording that the owner dismissed the past-due page in
 * this browser session. Value = the user id it applies to, so a different
 * account logging in on the same tab still gets the redirect. Cleared on logout.
 */
export const BILLING_DISMISSED_SESSION_KEY = "pm-billing-resolve-dismissed";

export function isBillingResolveDismissed(userId: string | undefined): boolean {
  if (!userId) return false;
  try {
    return sessionStorage.getItem(BILLING_DISMISSED_SESSION_KEY) === userId;
  } catch {
    return false;
  }
}

export function markBillingResolveDismissed(userId: string | undefined): void {
  if (!userId) return;
  try {
    sessionStorage.setItem(BILLING_DISMISSED_SESSION_KEY, userId);
  } catch {
    // storage unavailable — worst case the page shows again
  }
}

export function clearBillingResolveDismissed(): void {
  try {
    sessionStorage.removeItem(BILLING_DISMISSED_SESSION_KEY);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export type TierLabel =
  | { key: "tier.upTo"; params: { max: number } }
  | { key: "tier.range"; params: { min: number; max: number } }
  | { key: "tier.atLeast"; params: { min: number } };

/**
 * i18n key + params describing a tier's band, e.g.
 * {min 1, max 10} → "Up to 10 workers"; {11, 50} → "11–50 workers";
 * {51, null} → "51+ workers".
 */
export function tierLabel(tier: Pick<BillingTier, "minWorkers" | "maxWorkers">): TierLabel {
  const min = Math.max(0, tier.minWorkers);
  const max = tier.maxWorkers;
  if (max === null) return { key: "tier.atLeast", params: { min } };
  if (min <= 1) return { key: "tier.upTo", params: { max } };
  return { key: "tier.range", params: { min, max } };
}

/** "Visa •••• 4242", "Chase •••• 6789" (fallback label when brand is unknown). */
export function paymentMethodLabel(
  pm: Pick<BillingPaymentMethod, "type" | "brand" | "last4">,
  fallbacks: { card: string; bank: string },
): string {
  const name = pm.brand?.trim()
    ? pm.type === "card"
      ? capitalizeCardBrand(pm.brand.trim())
      : pm.brand.trim()
    : pm.type === "card"
      ? fallbacks.card
      : fallbacks.bank;
  return pm.last4 ? `${name} •••• ${pm.last4}` : name;
}

const CARD_BRAND_NAMES: Record<string, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  american_express: "American Express",
  discover: "Discover",
  diners: "Diners Club",
  jcb: "JCB",
  unionpay: "UnionPay",
};

function capitalizeCardBrand(brand: string): string {
  const known = CARD_BRAND_NAMES[brand.toLowerCase()];
  if (known) return known;
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

/** Minor units → localized currency string, e.g. 4900 "usd" → "$49.00". */
export function formatCents(
  cents: number,
  currency: string | null | undefined,
  lng: string,
): string {
  const code = (currency || "usd").toUpperCase();
  try {
    return new Intl.NumberFormat(localeFor(lng), {
      style: "currency",
      currency: code,
    }).format(cents / 100);
  } catch {
    return new Intl.NumberFormat(localeFor(lng), {
      style: "currency",
      currency: "USD",
    }).format(cents / 100);
  }
}

/** ISO instant → localized long date (date-fns default locale follows i18n). */
export function formatBillingDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    const d = parseISO(iso);
    if (Number.isNaN(d.getTime())) return null;
    return format(d, "PPP");
  } catch {
    return null;
  }
}

/** Date a trial of `trialDays` started now would end (for onboarding copy). */
export function trialEndFromNow(trialDays: number, now: Date = new Date()): Date {
  const d = new Date(now.getTime());
  d.setDate(d.getDate() + trialDays);
  return d;
}

// ---------------------------------------------------------------------------
// Cancellation / resolve-page variants
// ---------------------------------------------------------------------------

/** Subscription statuses meaning the subscription has ended. */
export function isSubscriptionEnded(
  status: BillingStatus["status"],
): boolean {
  return status === "canceled" || status === "incomplete_expired";
}

export type ResolveVariant =
  /** Nothing to resolve (or billing not enabled for the account). */
  | "resolved"
  /** Owner canceled themselves and the period ran out → offer to resume. */
  | "voluntaryEnded"
  /** Canceled for non-payment (or unknown reason) → payment-problem copy. */
  | "canceled"
  /** Past due / unpaid / incomplete → amount due, retry, remind me later. */
  | "paymentProblem";

/** Which version of the billing resolution page to render. */
export function resolveVariant(
  s: Pick<
    BillingStatus,
    "enabled" | "status" | "actionRequired" | "blocked" | "cancellationReason"
  >,
): ResolveVariant {
  if (!s.enabled || (!s.actionRequired && !s.blocked)) return "resolved";
  if (isSubscriptionEnded(s.status)) {
    return s.cancellationReason === "voluntary" ? "voluntaryEnded" : "canceled";
  }
  return "paymentProblem";
}

export type PlanScheduleLine =
  | { key: "settings.endsOn"; date: string }
  | { key: "settings.trialEnds"; date: string }
  | { key: "settings.nextBilling"; date: string }
  | { key: "settings.canceled" }
  | { key: "settings.noSubscriptionTrial"; count: number }
  | { key: "settings.noSubscription" };

/**
 * The schedule line on Settings → Billing's plan card (dates are ISO; the
 * caller formats them). A pending cancellation wins over trial / renewal.
 */
export function planScheduleLine(
  s: Pick<
    BillingStatus,
    | "status"
    | "trialEnd"
    | "currentPeriodEnd"
    | "trialDays"
    | "cancelAtPeriodEnd"
    | "cancelAt"
  >,
): PlanScheduleLine | null {
  if (s.cancelAtPeriodEnd && !isSubscriptionEnded(s.status)) {
    const date = s.cancelAt ?? s.currentPeriodEnd ?? s.trialEnd;
    if (date) return { key: "settings.endsOn", date };
  }
  if (s.status === "trialing" && s.trialEnd) {
    return { key: "settings.trialEnds", date: s.trialEnd };
  }
  if (s.status === "active" && s.currentPeriodEnd) {
    return { key: "settings.nextBilling", date: s.currentPeriodEnd };
  }
  if (isSubscriptionEnded(s.status)) return { key: "settings.canceled" };
  if (s.status === "none") {
    // Settings is only reachable after onboarding, so the subscription (and any
    // free trial) starts as soon as a payment method is added.
    return s.trialDays > 0
      ? { key: "settings.noSubscriptionTrial", count: s.trialDays }
      : { key: "settings.noSubscription" };
  }
  return null;
}
